// src/pages/AdminSubmissionReport.js

import React, {
  useEffect,
  useState,
  useMemo,
} from "react";

import {
  FaSearch,
  FaFileExcel,
  FaCircle,
  FaCalendarAlt,
  FaFilter,
  FaBuilding,
  FaUserTie,
} from "react-icons/fa";

import {
  FiRefreshCw,
} from "react-icons/fi";

import * as XLSX from "xlsx";

import dayjs from "dayjs";

import {
  getData,
} from "../../../services/FetchNodeservices";

export default function AdminSubmissionReport() {

  // =================================================
  // STATES
  // =================================================

  const [filters,
    setFilters] =
    useState({

      profile: "",

      company: "",

      location: "",

      search: "",

      date: "",

      status: "",

      priority: "",

      customFrom: "",

      customTo: "",
    });

  const [data,
    setData] =
    useState([]);

  const [companies,
    setCompanies] =
    useState([]);

  const [showFilters,
    setShowFilters] =
    useState(false);

  const [selectedRows,
    setSelectedRows] =
    useState([]);

  const [loading,
    setLoading] =
    useState(false);

  // =================================================
  // LOAD DATA
  // =================================================

  useEffect(() => {

    loadSubmissions();

    loadCompanies();

  }, []);

  const loadSubmissions =
    async () => {

      try {

        setLoading(true);

        const res =
          await getData(
            "allworkingtask/submissions"
          );

        if (
          res?.status
        ) {

          const processed =
            res.data.map(
              (r) => ({

                ...r,

                rowId:

                  r.id ||

                  r._id ||

                  r.task_id ||

                  Math.random(),

                employeename:
                  r.employeename

                    ? Array.isArray(
                        r.employeename
                      )

                      ? r.employeename

                      : String(
                          r.employeename
                        )
                          .split(",")
                          .map((x) =>
                            x.trim()
                          )

                    : [],
              })
            );

          setData(
            processed
          );
        }

      } catch (err) {

        console.log(err);

      } finally {

        setLoading(false);
      }
    };

  const loadCompanies =
    async () => {

      const res =
        await getData(
          "workingtask/companies"
        );

      if (
        res?.status
      ) {

        setCompanies(
          res.data
        );
      }
    };

  // =================================================
  // STATUS STYLE
  // =================================================

  const getStatusStyle =
    (status) => {

      switch (
        status
      ) {

        case "Completed":

          return "bg-green-100 text-green-700 border border-green-200";

        case "In Progress":

          return "bg-blue-100 text-blue-700 border border-blue-200";

        case "Expired":

          return "bg-red-100 text-red-700 border border-red-200";

        case "On Hold":

          return "bg-yellow-100 text-yellow-700 border border-yellow-200";

        default:

          return "bg-gray-100 text-gray-700 border border-gray-200";
      }
    };

  // =================================================
  // PRIORITY STYLE
  // =================================================

  const getPriorityColor =
    (priority) => {

      switch (
        priority
      ) {

        case "High":

          return "text-red-600";

        case "Medium":

          return "text-yellow-600";

        case "Low":

          return "text-green-600";

        default:

          return "text-gray-500";
      }
    };

  // =================================================
  // FILTER
  // =================================================

  const filteredData =
    useMemo(() => {

      return data.filter(
        (row) => {

          const empName =
            row.employeename
              .join(" ")
              .toLowerCase();

          const profileMatch =

            !filters.profile ||

            row.profile
              ?.toLowerCase()
              .includes(
                filters.profile.toLowerCase()
              );

          const companyMatch =

            !filters.company ||

            row.company ===
              filters.company;

          const locationMatch =

            !filters.location ||

            row.location
              ?.toLowerCase()
              .includes(
                filters.location.toLowerCase()
              );

          const statusMatch =

            !filters.status ||

            row.status ===
              filters.status;

          const priorityMatch =

            !filters.priority ||

            row.priority ===
              filters.priority;

          const searchMatch =

            !filters.search ||

            empName.includes(
              filters.search.toLowerCase()
            );

          let dateMatch =
            true;

          if (
            filters.date
          ) {

            const d =
              dayjs(
                row.date
              );

            const today =
              dayjs().startOf(
                "day"
              );

            if (
              filters.date ===
              "today"
            ) {

              dateMatch =
                d.isSame(
                  today,
                  "day"
                );
            }

            else if (
              filters.date ===
              "lastWeek"
            ) {

              dateMatch =
                d.isAfter(
                  today.subtract(
                    7,
                    "day"
                  )
                );
            }

            else if (
              filters.date ===
              "custom"
            ) {

              if (

                filters.customFrom &&

                d.isBefore(
                  dayjs(
                    filters.customFrom
                  )
                )

              ) {

                dateMatch =
                  false;
              }

              if (

                filters.customTo &&

                d.isAfter(
                  dayjs(
                    filters.customTo
                  )
                )

              ) {

                dateMatch =
                  false;
              }
            }
          }

          return (

            profileMatch &&

            companyMatch &&

            locationMatch &&

            statusMatch &&

            priorityMatch &&

            searchMatch &&

            dateMatch
          );
        }
      );

    }, [data, filters]);

  // =================================================
  // SELECT
  // =================================================

  const toggleRow =
    (id) => {

      setSelectedRows(
        (prev) =>

          prev.includes(id)

            ? prev.filter(
                (x) =>
                  x !== id
              )

            : [
                ...prev,
                id,
              ]
      );
    };

  const toggleAll =
    () => {

      if (

        selectedRows.length ===
        filteredData.length

      ) {

        setSelectedRows([]);

      } else {

        setSelectedRows(

          filteredData.map(
            (r) =>
              r.rowId
          )
        );
      }
    };

  // =================================================
  // EXPORT
  // =================================================

  const exportExcel =
    () => {

      const exportData =
        selectedRows.length

          ? filteredData.filter(
              (r) =>

                selectedRows.includes(
                  r.rowId
                )
            )

          : filteredData;

      if (
        !exportData.length
      ) {

        return alert(
          "No data to export"
        );
      }

      const excelRows =
        exportData.map(
          (row) => ({

            "Assign Date":
              dayjs(
                row.date
              ).format(
                "DD-MM-YYYY"
              ),

            "Due Date":
              row.due_date

                ? dayjs(
                    row.due_date
                  ).format(
                    "DD-MM-YYYY"
                  )

                : "N/A",

            Employee:
              row.employeename.join(
                ", "
              ),

            "Profile / Task":
              row.profile,

            Company:
              row.company,

            Location:
              row.location,

            Priority:
              row.priority,

            Status:
              row.status,

            "Target CV":
              row.target_cv,

            "CV Sourced":
              row.cv_sourced,
          })
        );

      const ws =
        XLSX.utils.json_to_sheet(
          excelRows
        );

      const wb =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(

        wb,

        ws,

        "AdminReport"
      );

      XLSX.writeFile(

        wb,

        "Task_Report.xlsx"
      );
    };

  // =================================================
  // UI
  // =================================================

  return (

    <div className="md:ml-72 pt-24 px-4 md:px-6 pb-8 min-h-screen text-slate-800">

      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <div
        className="
          flex
          flex-col
          lg:flex-row
          lg:items-center
          lg:justify-between
          gap-4
          mb-6
        "
      >

        <div>

          <h1
            className="
              text-3xl
              font-bold
              text-gray-800
            "
          >
            Master Task Report
          </h1>

          <p
            className="
              text-sm
              text-gray-500
              mt-1
            "
          >
            Track assignments, submissions & recruiter activity
          </p>
        </div>

        <div className="flex gap-3">

          <button
            onClick={
              loadSubmissions
            }
            className="
              bg-white
              border
              border-gray-200
              px-5
              py-3
              rounded-2xl
              text-sm
              font-medium
              flex
              items-center
              gap-2
              hover:bg-gray-50
            "
          >

            <FiRefreshCw />

            Refresh
          </button>

          <button
            onClick={
              exportExcel
            }
            className="
              bg-black
              text-white
              px-5
              py-3
              rounded-2xl
              text-sm
              font-medium
              flex
              items-center
              gap-2
            "
          >

            <FaFileExcel />

            Export
          </button>
        </div>
      </div>

      {/* ================================================= */}
      {/* MOBILE FILTER BUTTON */}
      {/* ================================================= */}

      <div className="lg:hidden mb-4">

        <button
          onClick={() =>

            setShowFilters(
              !showFilters
            )
          }
          className="
            flex
            items-center
            gap-2
            bg-black
            text-white
            px-4
            py-3
            rounded-2xl
            text-sm
          "
        >

          <FaFilter />

          Filters
        </button>
      </div>

      {/* ================================================= */}
      {/* FILTERS */}
      {/* ================================================= */}

      {(showFilters ||
        window.innerWidth >=
          1024) && (

        <div className="glass-card p-5 mb-6">

          <div
            className="
              grid
              grid-cols-1
              md:grid-cols-2
              lg:grid-cols-5
              gap-4
            "
          >

            {/* PROFILE */}

            <div className="glass-input flex items-center gap-3 rounded-2xl px-4 py-2.5">

              <FaSearch className="text-gray-400" />

              <input
                type="text"
                placeholder="Profile"
                className="w-full bg-transparent outline-none text-sm text-slate-800 dark:text-slate-200"
                value={
                  filters.profile
                }
                onChange={(e) =>

                  setFilters({

                    ...filters,

                    profile:
                      e.target.value,
                  })
                }
              />
            </div>

            {/* COMPANY */}

            <select
              className="glass-input px-4 py-2.5 rounded-2xl text-sm outline-none"
              value={
                filters.company
              }
              onChange={(e) =>

                setFilters({

                  ...filters,

                  company:
                    e.target.value,
                })
              }
            >

              <option value="">
                All Companies
              </option>

              {companies.map(
                (c, i) => (

                  <option
                    key={i}
                  >
                    {c}
                  </option>
                )
              )}
            </select>

            {/* EMPLOYEE */}

            <div className="glass-input flex items-center gap-3 rounded-2xl px-4 py-2.5">

              <FaUserTie className="text-gray-400" />

              <input
                type="text"
                placeholder="Employee"
                className="w-full bg-transparent outline-none text-sm text-slate-800 dark:text-slate-200"
                value={
                  filters.search
                }
                onChange={(e) =>

                  setFilters({

                    ...filters,

                    search:
                      e.target.value,
                  })
                }
              />
            </div>

            {/* STATUS */}

            <select
              className="glass-input px-4 py-2.5 rounded-2xl text-sm outline-none"
              value={
                filters.status
              }
              onChange={(e) =>

                setFilters({

                  ...filters,

                  status:
                    e.target.value,
                })
              }
            >

              <option value="">
                All Status
              </option>

              <option>
                Pending
              </option>

              <option>
                In Progress
              </option>

              <option>
                Completed
              </option>

              <option>
                Expired
              </option>
            </select>

            {/* DATE */}

            <select
              className="
                border
                border-gray-200
                rounded-2xl
                px-4
                py-3
                text-sm
              "
              value={
                filters.date
              }
              onChange={(e) =>

                setFilters({

                  ...filters,

                  date:
                    e.target.value,
                })
              }
            >

              <option value="">
                All Time
              </option>

              <option value="today">
                Today
              </option>

              <option value="lastWeek">
                Last 7 Days
              </option>

              <option value="custom">
                Custom
              </option>
            </select>
          </div>

          {/* CUSTOM DATE */}

          {filters.date ===
            "custom" && (

            <div
              className="
                grid
                grid-cols-1
                md:grid-cols-2
                gap-4
                mt-4
              "
            >

              <input
                type="date"
                className="
                  border
                  border-gray-200
                  rounded-2xl
                  px-4
                  py-3
                  text-sm
                "
                value={
                  filters.customFrom
                }
                onChange={(e) =>

                  setFilters({

                    ...filters,

                    customFrom:
                      e.target.value,
                  })
                }
              />

              <input
                type="date"
                className="
                  border
                  border-gray-200
                  rounded-2xl
                  px-4
                  py-3
                  text-sm
                "
                value={
                  filters.customTo
                }
                onChange={(e) =>

                  setFilters({

                    ...filters,

                    customTo:
                      e.target.value,
                  })
                }
              />
            </div>
          )}
        </div>
      )}

      {/* ================================================= */}
      {/* TABLE */}
      {/* ================================================= */}

      <div
        className="
          bg-white
          border
          border-gray-200
          rounded-3xl
          shadow-sm
          overflow-hidden
        "
      >

        <div className="overflow-auto">

          <table
            className="
              min-w-[1200px]
              w-full
            "
          >

            <thead
              className="
                bg-[#fafafa]
                border-b
                border-gray-200
              "
            >

              <tr>

                <th className="px-4 py-4 text-center">

                  <input
                    type="checkbox"
                    checked={

                      selectedRows.length ===
                        filteredData.length &&

                      filteredData.length >
                        0
                    }
                    onChange={
                      toggleAll
                    }
                  />
                </th>

                <th
                  className="
                    px-5
                    py-4
                    text-left
                    text-xs
                    uppercase
                    text-gray-500
                  "
                >
                  Date / Due
                </th>

                <th
                  className="
                    px-5
                    py-4
                    text-left
                    text-xs
                    uppercase
                    text-gray-500
                  "
                >
                  Employee & Task
                </th>

                <th
                  className="
                    px-5
                    py-4
                    text-left
                    text-xs
                    uppercase
                    text-gray-500
                  "
                >
                  Company
                </th>

                <th
                  className="
                    px-5
                    py-4
                    text-center
                    text-xs
                    uppercase
                    text-gray-500
                  "
                >
                  Priority
                </th>

                <th
                  className="
                    px-5
                    py-4
                    text-center
                    text-xs
                    uppercase
                    text-gray-500
                  "
                >
                  CV
                </th>

                <th
                  className="
                    px-5
                    py-4
                    text-center
                    text-xs
                    uppercase
                    text-gray-500
                  "
                >
                  Status
                </th>
              </tr>
            </thead>

            <tbody>

              {loading ? (

                <tr>

                  <td
                    colSpan="7"
                    className="
                      text-center
                      py-20
                      text-gray-500
                    "
                  >
                    Loading...
                  </td>
                </tr>

              ) : filteredData.length >
                0 ? (

                filteredData.map(
                  (
                    row,
                    i
                  ) => (

                    <tr
                      key={i}
                      className="
                        border-b
                        border-gray-100
                        hover:bg-gray-50
                      "
                    >

                      {/* CHECKBOX */}

                      <td
                        className="
                          px-4
                          py-4
                          text-center
                        "
                      >

                        <input
                          type="checkbox"
                          checked={

                            selectedRows.includes(
                              row.rowId
                            )
                          }
                          onChange={() =>

                            toggleRow(
                              row.rowId
                            )
                          }
                        />
                      </td>

                      {/* DATE */}

                      <td className="px-5 py-4">

                        <div className="font-semibold text-gray-800">

                          {dayjs(
                            row.date
                          ).format(
                            "DD MMM YYYY"
                          )}
                        </div>

                        <div
                          className="
                            text-xs
                            text-red-500
                            flex
                            items-center
                            gap-1
                            mt-1
                          "
                        >

                          <FaCalendarAlt size={10} />

                          {row.due_date

                            ? dayjs(
                                row.due_date
                              ).format(
                                "DD MMM YYYY"
                              )

                            : "N/A"}
                        </div>
                      </td>

                      {/* PROFILE */}

                      <td className="px-5 py-4">

                        <div
                          className="
                            font-semibold
                            text-gray-800
                            text-sm
                          "
                        >
                          {row.profile}
                        </div>

                        <div
                          className="
                            text-xs
                            text-gray-500
                            mt-1
                          "
                        >

                          {row.employeename.join(
                            ", "
                          )}
                        </div>
                      </td>

                      {/* COMPANY */}

                      <td className="px-5 py-4">

                        <div
                          className="
                            flex
                            items-center
                            gap-2
                            text-sm
                          "
                        >

                          <FaBuilding className="text-gray-400" />

                          {row.company}
                        </div>
                      </td>

                      {/* PRIORITY */}

                      <td
                        className="
                          px-5
                          py-4
                          text-center
                        "
                      >

                        <span
                          className={`
                            inline-flex
                            items-center
                            gap-1
                            text-sm
                            font-semibold

                            ${getPriorityColor(
                              row.priority
                            )}
                          `}
                        >

                          <FaCircle size={8} />

                          {row.priority}
                        </span>
                      </td>

                      {/* CV */}

                      <td
                        className="
                          px-5
                          py-4
                          text-center
                        "
                      >

                        <span
                          className="
                            text-sm
                            font-bold
                          "
                        >

                          {row.cv_sourced ||
                            0}

                          /

                          {row.target_cv ||
                            0}
                        </span>
                      </td>

                      {/* STATUS */}

                      <td
                        className="
                          px-5
                          py-4
                          text-center
                        "
                      >

                        <span
                          className={`
                            px-3
                            py-1
                            rounded-full
                            text-xs
                            font-semibold

                            ${getStatusStyle(
                              row.status
                            )}
                          `}
                        >

                          {row.status}
                        </span>
                      </td>
                    </tr>
                  )
                )

              ) : (

                <tr>

                  <td
                    colSpan="7"
                    className="
                      text-center
                      py-20
                      text-gray-400
                    "
                  >
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}